import { useEffect, useRef } from 'react';
import type { TranscriptItem } from '@/data/fixtures';

interface PodcastTranscriptProps {
  transcript: TranscriptItem[];
  /** Playhead position in seconds; drives the karaoke highlight. */
  currentTime: number;
  /** Seek the player to a line's start time. */
  onSeek: (time: number) => void;
}

/** The roles the podcast prompt gives each voice (`backend/src/podcast`):
 *  host_a asks the questions, host_b explains. */
const SPEAKER_LABEL: Record<NonNullable<TranscriptItem['speaker']>, string> = {
  host_a: 'Learner',
  host_b: 'Expert',
};

/**
 * Karaoke transcript for the podcast panel - grouped into speaker turns, the
 * spoken line is tinted, scrolls itself into view as playback advances, and
 * seeks the player when clicked.
 *
 * The tint is the brand accent (indigo in light, violet in dark), the same
 * family as the waveform's played bars, so the panel carries one accent.
 *
 * Extracted from `AudioRenderer` (where it was `TranscriptList`) so the
 * karaoke behaviour has a resting story per state driven by `currentTime`
 * alone. Named `PodcastTranscript` rather than `TranscriptList` because
 * `features/learning/video/TranscriptList` already owns that name.
 *
 * Shared by both the mock and API audio renderers, which is why it takes the
 * UI-shaped `TranscriptItem` (startTime / endTime) rather than the backend's
 * `PodcastTranscriptItem` - `AudioRenderer` normalizes at the boundary.
 */
export function PodcastTranscript({
  transcript,
  currentTime,
  onSeek,
}: PodcastTranscriptProps) {
  // The line currently being spoken (recomputed once per render, not per item).
  const currentItem = getCurrentTranscriptItem(currentTime, transcript);
  // Karaoke auto-scroll: keep the spoken line in view as playback advances.
  const activeRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [currentItem?.id]);

  return (
    // Section is full-width so the scroll container's scrollbar lands flush
    // against the panel border; the column inside carries the padding.
    <div className='flex min-h-0 flex-1 flex-col'>
      {/* `68ch` is measured in the wrapper's body size, not the smaller mono
          eyebrow, so the label lines up with the transcript column below. */}
      <div className='px-6 sm:px-10'>
        <div className='mx-auto max-w-[68ch] text-base'>
          <p className='mb-3 font-mono text-label font-normal text-muted-foreground/90'>
            Transcript
          </p>
        </div>
      </div>
      {/* Focusable and named: this is a scroll container, and without
          `tabIndex` a keyboard user cannot scroll the transcript at all —
          there is nothing tabbable inside it either, since the lines are
          spans. axe reports it as `scrollable-region-focusable`. */}
      <div
        className='min-h-0 flex-1 overflow-y-auto'
        tabIndex={0}
        role='region'
        aria-label='Podcast transcript'
      >
        <div className='px-6 pb-8 sm:px-10'>
          <div className='mx-auto max-w-[68ch] space-y-4 text-base leading-relaxed'>
            {groupTurns(transcript).map(turn => (
              <div key={turn.items[0].id}>
                {turn.speaker && (
                  <p className='mb-1 font-mono text-xs uppercase tracking-wider text-muted-foreground/90'>
                    {SPEAKER_LABEL[turn.speaker]}
                  </p>
                )}
                <p>
                  {turn.items.map((item, index) => {
                    const isCurrent = currentItem?.id === item.id;
                    return (
                      <span
                        key={item.id}
                        ref={isCurrent ? activeRef : undefined}
                        // The spoken line was conveyed by background tint
                        // ALONE, invisible to assistive tech. `aria-current`
                        // is the same fix applied to `LessonTabs`.
                        aria-current={isCurrent ? 'true' : undefined}
                        // No shadow: a highlight inside running prose is a
                        // tint, not an elevated surface.
                        className={`inline cursor-pointer rounded-sm transition-colors duration-300 ease-in-out ${
                          isCurrent
                            ? 'bg-brand-100 px-1 py-0.5 text-foreground dark:bg-primary/20'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                        onClick={() => onSeek(item.startTime)}
                      >
                        {item.text}
                        {index < turn.items.length - 1 && ' '}
                      </span>
                    );
                  })}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

interface Turn {
  speaker?: TranscriptItem['speaker'];
  items: TranscriptItem[];
}

/**
 * Consecutive lines by the same voice become one turn, so the transcript reads
 * as a conversation instead of one undivided wall of text. Lines without a
 * speaker all fall into a single unlabelled turn.
 */
function groupTurns(transcript: TranscriptItem[]): Turn[] {
  const turns: Turn[] = [];
  for (const item of transcript) {
    const last = turns[turns.length - 1];
    if (last && last.speaker === item.speaker) last.items.push(item);
    else turns.push({ speaker: item.speaker, items: [item] });
  }
  return turns;
}

function getCurrentTranscriptItem(
  currentTime: number,
  transcriptData: TranscriptItem[]
) {
  return transcriptData.find(
    item =>
      currentTime >= item.startTime &&
      (item.endTime ? currentTime < item.endTime : true)
  );
}
